import Badge from "@/components/Badge";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import Input from "@/components/Input";
import Pagination from "@/components/Pagination";
import Table from "@/components/Table";
import BodyColumn from "@/components/Table/table-body/BodyColumn";
import BodyImageWithInfoColumn from "@/components/Table/table-body/BodyImageWithInfoColumn";
import HeaderColumn from "@/components/Table/table-header/HeaderColumn";
import NO_PROFILE_PIC from "@/assets/icons/avatar.svg";
import { ReactComponent as EDIT_ICON } from "@/assets/icons/edit-black.svg";
import { ReactComponent as TRASH_ICON } from "@/assets/icons/trash.svg";
import { useNavigate } from "react-router-dom";
import getUserInteractor from "@/@core/interactor/user/get-users.interactor";
import { ChangeEvent, useEffect, useState } from "react";
import { User } from "@/@core/entity/user.entity";
import { DROPDOWN_ROLE_OPTIONS } from "../../constant";
import { OptionItem } from "@/components/Dropdown/model";
import { stringToDate } from "@/utils/date";
import Modal from "@/components/Modal";
import toast, { Toaster } from "react-hot-toast";
import deleteUserInteractor from "@/@core/interactor/user/delete-user.interactor";

let debounceSearch: any;
let selectedUser: User;
let IndexSelectedUSer: number = -1;

const TableSection = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState<string>("");
  const [selectedRole, setSelectedRole] = useState<OptionItem>(
    DROPDOWN_ROLE_OPTIONS[0]
  );
  const [userList, setUserList] = useState<User[]>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [isShowModalDelete, setIsShowModalDelete] = useState<boolean>(false);

  useEffect(() => {
    getUserList(activePage);
  }, []);

  const getUserList = async (page: number, keyword = "", role = "") => {
    try {
      const response = await getUserInteractor({
        page,
        keyword,
        role: role === "all" ? "" : role,
        limit: 5,
      });

      const {
        data: { data, pagination },
      } = response;

      setUserList(data);
      if (pagination) {
        setActivePage(pagination.current_page);
        setTotalPage(pagination.last_page);
      }
    } catch (err) {
      setUserList([]);
      setActivePage(1);
      setTotalPage(1);
    }
  };

  const onChangeKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setKeyword(val);

    clearTimeout(debounceSearch);
    debounceSearch = setTimeout(() => {
      getUserList(1, val, selectedRole.value);
    }, 2000);
  };

  const onKeywordEnter = (e: any) => {
    const keyCode = e.keyCode;
    const val = e.target.value;

    if (keyCode === 13) {
      clearTimeout(debounceSearch);
      getUserList(1, val, selectedRole.value);
    }
  };

  const onChangeRole = (e: OptionItem) => {
    setSelectedRole(e);
    getUserList(1, keyword, e.value);
  };

  const resetFilter = () => {
    getUserList(1, "", DROPDOWN_ROLE_OPTIONS[0].value);
    setKeyword("");
    setSelectedRole(DROPDOWN_ROLE_OPTIONS[0]);
  };

  const onChangePagination = (e: any) => {
    setActivePage(e);
    getUserList(e, keyword, selectedRole.value);
  };

  const deleteUser = async () => {
    try {
      const { id, fullname } = selectedUser;
      const response = await deleteUserInteractor({ userId: id });

      const { status } = response;

      if (status === 200) {
        if (activePage === 1) {
          getUserList(1, keyword, selectedRole.value);
        } else {
          const tempUsers = JSON.parse(JSON.stringify(userList));
          tempUsers.splice(IndexSelectedUSer, 1);
          setUserList(tempUsers);

          if (tempUsers.length === 0) {
            getUserList(activePage - 1, keyword, selectedRole.value);
          }
        }

        setIsShowModalDelete(false);
        toast.success(`${fullname} has been deleted`);
      }
    } catch (error: any) {
      const { response } = error;
      const {
        data: { message },
      } = response;

      toast.error(message);
    }
  };

  return (
    <>
      <div className="table-control-wrapper">
        <div>
          <label>Search for User</label>
          <Input
            style={{ width: "360px" }}
            value={keyword}
            withIcon
            type="search"
            placeholder="Search"
            onChange={onChangeKeyword}
            onKeyDown={onKeywordEnter}
          />
        </div>
        <div>
          <label>Roles</label>
          <Dropdown
            value={selectedRole}
            options={DROPDOWN_ROLE_OPTIONS}
            onChange={(e) => onChangeRole(e)}
            optionType="text-option"
            width="192px"
          />
        </div>

        <div className="btn-wrapper">
          <Button
            onClick={resetFilter}
            label="Reset Filter"
            type="secondary"
            size="md"
            disabled={!keyword && selectedRole.value === "all"}
          />
        </div>
      </div>

      <div className="table-section">
        <Table>
          <thead>
            <tr>
              <HeaderColumn label="Product ID" alignLabel="text-left" />
              <HeaderColumn label="Role" alignLabel="text-left" />
              <HeaderColumn label="Product" alignLabel="text-center" />
              <HeaderColumn label="Created at" alignLabel="text-center" />
              <HeaderColumn label="Last Active" alignLabel="text-center" />
              <HeaderColumn label="" alignLabel="text-left" />
            </tr>
          </thead>
          <tbody>
            {userList.map((user, index) => (
              <tr key={user.id}>
                <BodyImageWithInfoColumn
                  width="200px"
                  imgSrc={
                    user.photo_profile
                      ? `${process.env.REACT_APP_API_URL}/file/${user.photo_profile}`
                      : NO_PROFILE_PIC
                  }
                  roundedImg="20px"
                  textName={user.fullname}
                  textDesc={user.email}
                  onClick={() =>
                    navigate(`/users/user-detail/${user.id}`, {
                      state: { isShowActivityInfo: true, showBio: false },
                    })
                  }
                />
                <td width="100px">
                  <div className="item-center">
                    <Badge
                      label={user.role === 'super_admin' ? 'super admin' : user.role}
                      backgroundColor="#F2F4F7"
                      labelColor="#344054"
                    />
                  </div>
                </td>
                <BodyColumn
                  width="100px"
                  label={`${user.total_claim - user.total_transfer}`}
                  alignLabel="text-center"
                />
                <BodyColumn
                  width="100px"
                  label={stringToDate(user.created_at, "dd month yyyy")}
                  alignLabel="text-center"
                />
                <BodyColumn
                  width="100px"
                  label={"-"}
                  alignLabel="text-center"
                />
                <td width="100px">
                  <div className="cta-wrapper">
                    <button
                      onClick={() => {
                        setIsShowModalDelete(true);
                        selectedUser = user;
                        IndexSelectedUSer = index;
                      }}
                    >
                      <TRASH_ICON />
                    </button>
                    <button
                      onClick={() =>
                        navigate("/users/edit-user", {
                          state: { procedure: "edit", userId: user.id },
                        })
                      }
                    >
                      <EDIT_ICON />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Pagination
          activePage={activePage}
          totalPage={totalPage}
          onChange={onChangePagination}
        />

        {isShowModalDelete && (
          <div className="delete-user-modal-wrapper">
            <Modal
              header={"header"}
              content={
                <div className="content-delete-product">
                  <h2 className="modal-title">Delete User</h2>
                  <span className="desc">
                    Are you sure you want to delete this user? This action
                    cannot be undone.
                  </span>
                </div>
              }
              type="delete"
              setIsOpen={() => setIsShowModalDelete(!isShowModalDelete)}
              footer={
                <div className="delete-product-modal-btn-wrapper">
                  <Button
                    onClick={() => setIsShowModalDelete(false)}
                    label="Cancel"
                    type="secondary"
                    size="md"
                  />
                  <Button
                    onClick={deleteUser}
                    label="Delete"
                    type="warning"
                    size="md"
                  />
                </div>
              }
            />
          </div>
        )}
      </div>

      <Toaster />
    </>
  );
};

export default TableSection;
